'use client';

import type { SortDirection } from '@morpho-org/ui-v6/client';
import { InterfaceChainUtils } from '@morpho-org/dapp-core';
import { isDefined } from '@morpho-org/morpho-ts';
import { TableNoFilterResults } from '@morpho-org/ui-v6/client';
import { CompactTable, computeActiveCount, type OptionTableProperties, SortBy, TablePropertiesPopover } from '@morpho-org/ui-v6/client';
import { useIsBelowBreakpoint } from '@morpho-org/ui-v6/hooks/useMediaQuery';
import { cn } from '@morpho-org/ui-v6/utils';
import { Button, Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui';
import { BREAKPOINTS_PX } from '@repo/ui/breakpoints';
import { Collapse, Expand } from '@repo/ui/icons';
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { flexRender, useTable } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TableQueryError } from '@/components/common/Error';
import { InternalLink } from '@/components/common/InternalLink';
import { InWalletFilter } from '@/components/common/InWalletFilter';
import { SllBadge } from '@/components/common/SllBadge';
import { TableNetworkFilter } from '@/components/common/TableAdvancedFilters';
import AssetFilter from '@/components/common/TableAdvancedFilters/AssetFilter';
import { ChainConfig } from '@/config';
import { useWeb3Context } from '@/contexts/Web3Context';
import useTableSettings, { type TableSettingsProps } from '@/helpers/cookieStorage/userSettings/tableSettings/client';
import { PageKey } from '@/helpers/cookieStorage/userSettings/types';
import { curatorsToFilterItems } from '@/helpers/filter/vaultMasterFilter/getFacetFilters';
import { getInfiniteSkeletonRowCount } from '@/helpers/getTableSkeletonRowCount';
import { getMarketUrl } from '@/helpers/routing';
import { useInfiniteTableScroll } from '@/hooks/useInfiniteTableScroll';
import { useInWalletFilter } from '@/hooks/useInWalletFilter';
import { useMasterFilterTracking } from '@/lib/analytics/client/hooks/useMasterFilterTracking';
import { trackAnalyticsEvent } from '@/lib/analytics/client/track';
import { trackColumnToggled } from '@/lib/analytics/client/trackers/column';
import { resetSllExploreImpressions } from '@/lib/analytics/client/trackers/sll';
import { trackSortChanged } from '@/lib/analytics/client/trackers/sort';
import { MarketsMasterFilter } from './components/MarketsMasterFilter';
import MarketTableFilters from './components/MarketTableFilters';
import MobileMarketCardList from './components/MobileMarketCardList';
import { COLUMN_IDS, DEFAULT_MARKETS_MASTER_FILTER_STATE, MARKETS_COLUMN_LABELS, MARKETS_DEFAULT_TABLE_SETTINGS, MARKETS_INITIAL_SORTING, MARKETS_MASTER_FILTER_EXCLUDE_KEYS, MARKETS_PAGE_SIZE, MARKETS_STORAGE_KEY, type MarketsFilters, type MarketsMasterFilterState, ROW_HEIGHT_ESTIMATE, SORT_BY_OPTIONS, VIRTUALIZER_INITIAL_RECT, VIRTUALIZER_OVERSCAN } from './config/config';
import { isMarketListInputWithinBounds, marketListInfiniteOptions, toMarketListInput } from './config/marketListQuery';
import { marketColumnsDefs, tableMarketFeatures } from './config/TableMarket.columns';
import { getMarketFacetFilters } from './getMarketFacetFilters';
import { useMarketsMasterFilter, useMarketsTableSorting, useTableMarketRow, useUserMarketPositions } from './hooks';
export interface TableMarketClientProps {
  initialTableSettings: TableSettingsProps['initialTableSettings'];
  /** Request-derived phone guess (`getIsMobileRequest`), used until `matchMedia` takes over after hydration. */
  initialIsMobile: boolean;
}
const FILTER_TOO_LONG_MESSAGE = 'Too many filters selected. Remove some to load markets.';
const TableMarketClient = ({
  initialTableSettings,
  initialIsMobile
}: TableMarketClientProps) => {
  useEffect(() => resetSllExploreImpressions, []);
  const isMobile = useIsBelowBreakpoint(BREAKPOINTS_PX.md, {
    serverSnapshot: initialIsMobile
  });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isFullscreenHovered, setIsFullscreenHovered] = useState(false);
  const {
    account
  } = useWeb3Context();
  const {
    urlParams: filters,
    setUrlParams: setFilters,
    masterFilterState,
    onMasterFilterChange,
    isTransitionPending,
    startTransition
  } = useMarketsMasterFilter();
  const masterFilterTracking = useMasterFilterTracking('markets');
  const {
    sorting,
    setSorting,
    columnVisibility,
    setColumnVisibility,
    setColumnOrder,
    columnOrder
  } = useTableSettings({
    pageKey: PageKey.Borrow,
    tableSettingsObjectKey: MARKETS_STORAGE_KEY,
    defaultTableSettings: MARKETS_DEFAULT_TABLE_SETTINGS,
    initialTableSettings
  });
  const {
    orderBy,
    desc
  } = useMarketsTableSorting(sorting);
  const sortingItem = sorting[0];
  const sortByDirection: SortDirection = sortingItem?.desc ? 'desc' : 'asc';
  const isDefaultSorting = sortingItem?.id === MARKETS_INITIAL_SORTING[0]!.id && sortingItem?.desc === MARKETS_INITIAL_SORTING[0]!.desc;
  const handleSortByOptionChange = (value: string) => {
    const nextDesc = sortingItem?.desc ?? true;
    setSorting([{
      id: value,
      desc: nextDesc
    }]);
    if (columnVisibility[value] === false) {
      setColumnVisibility(prev => ({
        ...prev,
        [value]: true
      }));
    }
    trackSortChanged({
      page: 'markets',
      sortBy: value,
      sortDirection: nextDesc ? 'desc' : 'asc',
      trigger: 'option',
      isDefault: value === MARKETS_INITIAL_SORTING[0]!.id && nextDesc === MARKETS_INITIAL_SORTING[0]!.desc
    });
  };
  const handleSortByDirectionChange = (direction: SortDirection) => {
    const nextSortBy = sortingItem?.id ?? MARKETS_INITIAL_SORTING[0]!.id;
    setSorting([{
      id: nextSortBy,
      desc: direction === 'desc'
    }]);
    trackSortChanged({
      page: 'markets',
      sortBy: nextSortBy,
      sortDirection: direction,
      trigger: 'direction',
      isDefault: nextSortBy === MARKETS_INITIAL_SORTING[0]!.id && direction === 'desc' === MARKETS_INITIAL_SORTING[0]!.desc
    });
  };
  const handleSortByClear = () => {
    setSorting(MARKETS_INITIAL_SORTING);
    const defaultSort = MARKETS_INITIAL_SORTING[0]!;
    trackSortChanged({
      page: 'markets',
      sortBy: defaultSort.id,
      sortDirection: defaultSort.desc ? 'desc' : 'asc',
      trigger: 'clear',
      isDefault: true
    });
  };
  const listInput = toMarketListInput(filters, {
    orderBy,
    desc
  });
  const isFilterTooLong = !isMarketListInputWithinBounds(listInput);
  const listOptions = marketListInfiniteOptions(listInput);
  const infiniteQueryKey = listOptions.queryKey;
  const {
    data: infiniteData,
    isLoading: isQueryLoading,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error
  } = useInfiniteQuery({
    ...listOptions,
    enabled: !isFilterTooLong,
    placeholderData: keepPreviousData
  });
  const allMarkets = useMemo(() => infiniteData?.pages.flatMap(page => page.items) ?? [], [infiniteData]);
  const firstPage = infiniteData?.pages[0];
  const facets = firstPage?.facets ?? undefined;
  const isPartial = infiniteData?.pages.some(page => page.partial) ?? false;
  const {
    filteredCollateralAssets,
    filteredLoanAssets,
    filteredCurators,
    filteredChainCounts,
    filteredChainTvls
  } = useMemo(() => getMarketFacetFilters({
    facets
  }), [facets]);
  const {
    isLoading: isWalletFilterLoading,
    isActive: isWalletFilterActive,
    isError: isWalletFilterError,
    toggle: toggleWalletFilter,
    reset: resetInWalletFilter,
    holdingsSelected
  } = useInWalletFilter({
    assets: filteredCollateralAssets,
    currentAssets: filters.collateralAssetIdsFilter,
    onChange: assetSelectors => {
      onFiltersChange({
        ...filters,
        collateralAssetIdsFilter: assetSelectors
      });
    }
  });
  const inWalletFilterEmptyState = isWalletFilterActive && !isWalletFilterLoading && !isWalletFilterError && holdingsSelected.length === 0;
  const curatorItems = curatorsToFilterItems(filteredCurators, masterFilterState.curators);
  const userMarketPositionKeys = useUserMarketPositions();
  const marketRows = useTableMarketRow({
    markets: allMarkets,
    userMarketPositionKeys
  });
  const table = useTable({
    features: tableMarketFeatures,
    data: marketRows,
    columns: marketColumnsDefs,
    state: {
      sorting,
      columnVisibility,
      columnOrder
    },
    manualSorting: true,
    enableSortingRemoval: false,
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder
  });
  const onFiltersChange = (newFilters: MarketsFilters) => {
    startTransition(() => {
      setFilters(newFilters);
    });
  };
  const handleSearchChange = (value: string) => {
    onFiltersChange({
      ...filters,
      search: value.trim()
    });
  };
  const isFilterFetching = isFetching && !isQueryLoading && !isFetchingNextPage;
  const isLoadingFullTable = isFilterFetching || isWalletFilterLoading || isTransitionPending;
  const {
    sentinelRef,
    scrollContainerRef
  } = useInfiniteTableScroll({
    queryKey: infiniteQueryKey,
    hasNextPage,
    isFetchingNextPage,
    isLoadingFullTable,
    fetchNextPage,
    enabled: !inWalletFilterEmptyState,
    isMobile
  });

  // Track what the table shows. Zero when the wallet filter resolves to no holdings, since the
  // API then sees no asset restriction and keeps returning the global total. Blank while a
  // filter change is in flight, since keepPreviousData holds the previous filter's count, and
  // blank rather than undefined so AdvancedFilter does not fall back to its "N selected".
  const shownResultCount = inWalletFilterEmptyState ? 0 : isLoadingFullTable ? undefined : firstPage?.countTotal;
  const filterResultLabel = shownResultCount == null ? '' : `${shownResultCount.toLocaleString('en-US')} ${shownResultCount === 1 ? 'market' : 'markets'}`;
  const skeletonRowCount = getInfiniteSkeletonRowCount(MARKETS_PAGE_SIZE, {
    isLoadingFullTable: isQueryLoading || isLoadingFullTable,
    isFetchingNextPage,
    totalCount: firstPage?.countTotal,
    loadedCount: allMarkets.length
  });
  const skeletonRows = useMemo(() => Array.from({
    length: skeletonRowCount
  }, () => marketColumnsDefs.filter(def => {
    if (!isDefined(columnVisibility) || !isDefined(def?.id)) return true;
    if (!isDefined(columnVisibility[def.id])) return true;
    return columnVisibility[def.id];
  }).map(def => def.skeletonCell)), [skeletonRowCount, columnVisibility]);
  const dataRows = table.getRowModel().rows;
  const rowVirtualizer = useVirtualizer({
    count: isLoadingFullTable ? 0 : dataRows.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => ROW_HEIGHT_ESTIMATE,
    overscan: VIRTUALIZER_OVERSCAN,
    initialRect: VIRTUALIZER_INITIAL_RECT
  });
  const virtualRows = rowVirtualizer.getVirtualItems();
  const paddingTop = virtualRows[0]?.start ?? 0;
  const paddingBottom = virtualRows.length > 0 ? rowVirtualizer.getTotalSize() - (virtualRows.at(-1)?.end ?? 0) : 0;
  const columns = columnOrder.length > 0 ? columnOrder : table.getAllLeafColumns().map(column => column.id);
  const columnOptions = columns.map(column => {
    const label = MARKETS_COLUMN_LABELS[column];
    if (!isDefined(label)) return null;
    const isActive = isDefined(columnVisibility[column]) ? !!columnVisibility[column] : true;
    return {
      label,
      value: column,
      isActive,
      // The loan asset is the market's headline identity, so it is the one column
      // the user cannot hide. Hiding it would leave a list read by collateral alone.
      isDisabled: column === COLUMN_IDS.LoanAssetSymbol,
      action: () => {
        table.getColumn(column)?.toggleVisibility();
        trackColumnToggled({
          page: 'markets',
          column,
          isVisible: !isActive
        });
      }
    };
  }).filter(isDefined);
  const handleColumnReorder = (orderedOptions: OptionTableProperties<string>[]) => {
    table.setColumnOrder(orderedOptions.map(option => option.value));
  };
  const isInitialLoading = isQueryLoading && !isFilterTooLong || isWalletFilterLoading || isTransitionPending || isFilterFetching;
  const hasError = isFilterTooLong || error || isWalletFilterError && isWalletFilterActive;
  const hasRows = !inWalletFilterEmptyState && table.getRowModel().rows.length > 0;
  const showTable = (hasRows || isInitialLoading || isFetchingNextPage) && !hasError;
  const showEmptyState = !hasRows && !hasError && !isInitialLoading;
  const hasFilters = !!filters.search || filters.collateralAssetIdsFilter.length > 0 || filters.loanAssetIdsFilter.length > 0 || filters.curators.length > 0 || !!filters.chains.length || isWalletFilterActive || filters.sizeUsd_gte != null || filters.sizeUsd_lte != null || filters.totalLiquidityUsd_gte != null || filters.totalLiquidityUsd_lte != null || filters.avgNetBorrowApy_gte != null || filters.avgNetBorrowApy_lte != null || filters.lltv_gte != null || filters.lltv_lte != null;
  const clearFilters = () => {
    resetInWalletFilter();
    onFiltersChange({
      ...filters,
      search: '',
      chains: [],
      collateralAssetIdsFilter: [],
      loanAssetIdsFilter: [],
      curators: [],
      sizeUsd_gte: null,
      sizeUsd_lte: null,
      totalLiquidityUsd_gte: null,
      totalLiquidityUsd_lte: null,
      avgNetBorrowApy_gte: null,
      avgNetBorrowApy_lte: null,
      lltv_gte: null,
      lltv_lte: null
    });
  };
  const prevAddressRef = useRef(account?.address);
  useEffect(() => {
    if (prevAddressRef.current !== undefined && prevAddressRef.current !== account?.address) {
      clearFilters();
    }
    prevAddressRef.current = account?.address;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only trigger on wallet change
  }, [account?.address]);
  const isFullscreenRef = useRef(isFullscreen);
  isFullscreenRef.current = isFullscreen;
  const toggleFullscreen = useCallback(() => {
    const next = !isFullscreenRef.current;
    trackAnalyticsEvent('table_fullscreen_toggled', {
      page: 'markets',
      is_fullscreen: next,
      trigger: 'button'
    });
    setIsFullscreen(next);
  }, []);
  useEffect(() => {
    if (!isFullscreen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        trackAnalyticsEvent('table_fullscreen_toggled', {
          page: 'markets',
          is_fullscreen: false,
          trigger: 'keyboard'
        });
        setIsFullscreen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen]);
  return <div data-testid="listing-table" className="gap-sm flex min-h-0 w-full flex-1 flex-col max-md:gap-0">
      <div className="gap-sm flex min-h-0 flex-1 flex-col max-md:gap-0">
        <div className={cn('shadow-md dark:shadow-none border-[0.5px] border-border max-md:bg-background bg-card flex min-h-0 flex-1 flex-col rounded-[20px] max-md:rounded-none max-md:border-none max-md:shadow-none', isFullscreen && 'z-50 fixed top-[var(--navbar-height,_70px)] right-[var(--frame-border-size,_10px)] bottom-[var(--frame-border-size,_10px)] left-[var(--frame-border-size,_10px)]')}>
          <div className="max-md:bg-background bg-card sticky z-1 overflow-hidden rounded-t-[20px] max-md:top-[var(--navbar-height)]">
            <div data-testid="listing-filters" className="p-md box-border content-center rounded-t-[20px] max-md:rounded-none max-md:border-none max-md:bg-transparent max-md:p-[16px]">
              <MarketTableFilters holdingsFilterSlot={account.isConnected ? <InWalletFilter isActive={isWalletFilterActive} onChange={toggleWalletFilter} tooltipContent="Filter markets by wallet holdings." page="markets" /> : undefined} networkFilterSlot={<TableNetworkFilter chainIdsFilter={masterFilterState.chains} handleChainIdsFilterChange={chains => onMasterFilterChange({
              ...masterFilterState,
              chains
            })} disabled={isWalletFilterLoading} chainCounts={filteredChainCounts} chainTvls={filteredChainTvls} hasHighestZIndex={isFullscreen} resultLabel={filterResultLabel} page="markets" />} collateralFilterSlot={<AssetFilter label="collateral" allLabel="Collateral" allAssets={filteredCollateralAssets} assets={masterFilterState.collateralAssets} chainIds={masterFilterState.chains.length > 0 ? masterFilterState.chains : ChainConfig.AVAILABLE_CHAIN_IDS} onAssetsChange={collateralAssets => {
              const next = {
                ...masterFilterState,
                collateralAssets
              };
              onMasterFilterChange(next);
              masterFilterTracking.onFieldChange('collateralAssets', computeActiveCount(next, DEFAULT_MARKETS_MASTER_FILTER_STATE, MARKETS_MASTER_FILTER_EXCLUDE_KEYS));
            }} disabled={isWalletFilterLoading} hasHighestZIndex={isFullscreen} resultLabel={filterResultLabel} showPTFilter />} loanFilterSlot={<AssetFilter label="loan" allLabel="Loan" allAssets={filteredLoanAssets} assets={masterFilterState.loanAssets} chainIds={masterFilterState.chains.length > 0 ? masterFilterState.chains : ChainConfig.AVAILABLE_CHAIN_IDS} onAssetsChange={loanAssets => {
              const next = {
                ...masterFilterState,
                loanAssets
              };
              onMasterFilterChange(next);
              masterFilterTracking.onFieldChange('loanAssets', computeActiveCount(next, DEFAULT_MARKETS_MASTER_FILTER_STATE, MARKETS_MASTER_FILTER_EXCLUDE_KEYS));
            }} disabled={isWalletFilterLoading} hasHighestZIndex={isFullscreen} resultLabel={filterResultLabel} showPTFilter />} masterFilterSlot={<MarketsMasterFilter value={masterFilterState} onChange={(newState: MarketsMasterFilterState) => {
              onMasterFilterChange(newState);
            }} curatorItems={curatorItems} />} sortBySlot={<SortBy options={SORT_BY_OPTIONS} selectedOption={sortingItem?.id ?? null} direction={sortByDirection} onOptionChange={handleSortByOptionChange} onDirectionChange={handleSortByDirectionChange} onClear={handleSortByClear} clearable={!isDefaultSorting} />} editPropertiesSlot={!isMobile ? <TablePropertiesPopover options={columnOptions} setColumnOrder={handleColumnReorder} /> : undefined} fullscreenSlot={!isMobile ? <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="ghost" size="iconSm" aria-label={isFullscreen ? 'Exit full screen' : 'Browse in full screen'} onClick={toggleFullscreen} onMouseEnter={() => setIsFullscreenHovered(true)} onMouseLeave={() => setIsFullscreenHovered(false)}>
                          {isFullscreen ? <Collapse isExpanded={!isFullscreenHovered} /> : <Expand isExpanded={isFullscreenHovered} />}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        {isFullscreen ? 'Exit full screen' : 'Browse in full screen'}
                      </TooltipContent>
                    </Tooltip> : undefined} searchValue={filters.search} onSearchChange={handleSearchChange} disabled={isWalletFilterLoading} />
            </div>
          </div>

          {isPartial && !hasError && <p data-testid="market-table-partial-notice" className="text-secondary-foreground px-md pb-sm text-sm max-md:px-[16px]">
              Some markets could not be loaded, so this list may be incomplete.
            </p>}

          {isMobile ? <div className="mx-[10px]">
              {showTable && <MobileMarketCardList rows={isLoadingFullTable ? [] : table.getRowModel().rows} isLoading={isInitialLoading} isFetchingMore={isFetchingNextPage} skeletonCount={skeletonRowCount} />}

              {showEmptyState && <TableNoFilterResults type="markets" hasFilters={hasFilters} clearFilters={clearFilters} minHeight="250px" />}

              {hasError && (isFilterTooLong ? <TableNoFilterResults type="markets" hasFilters clearFilters={clearFilters} minHeight="250px" message={FILTER_TOO_LONG_MESSAGE} /> : <TableQueryError error={error} minHeight="250px" style={{
            borderRadius: '0',
            backgroundColor: 'transparent'
          }} />)}

              {hasNextPage && !hasError && !inWalletFilterEmptyState && <div ref={sentinelRef} className="h-px" />}
            </div> : <div className="border-border grid min-h-0 flex-1 grid-cols-[minmax(0,1fr)] overflow-hidden rounded-b-[20px] border-t-[0.5px]">
              <div ref={scrollContainerRef} className="max-h-full min-h-0 overflow-auto overscroll-y-contain" data-testid="table-scroll-container">
                {showTable && <CompactTable className="table-fixed">
                    <CompactTable.Head data-testid="listing-table-header" headerGroups={table.getHeaderGroups()} columnLabels={MARKETS_COLUMN_LABELS} onColumnOrderChange={table.setColumnOrder} columnOrder={columns} />
                    <CompactTable.Body>
                      {!isLoadingFullTable && <>
                          {paddingTop > 0 && <tr>
                              <td colSpan={table.getVisibleLeafColumns().length} style={{
                      height: paddingTop
                    }} />
                            </tr>}
                          {virtualRows.map(virtualRow => {
                    const row = dataRows[virtualRow.index];
                    if (!row) return null;
                    const href = getMarketUrl({
                      identifier: InterfaceChainUtils.getIdentifierById(row.original.market.chainId),
                      marketId: row.original.market.marketId,
                      collateralSymbol: row.original.market.collateralAsset?.symbol ?? '',
                      loanSymbol: row.original.market.loanAsset.symbol ?? ''
                    });
                    return <CompactTable.Row key={row.id}>
                                {row.getVisibleCells().map(cell => <CompactTable.Cell key={cell.id}>
                                    {cell.column.id === COLUMN_IDS.TotalLiquidityUsd ?
                        // A button can't nest in the anchor, so the link stretches
                        // over the cell and the badge sits above it.
                        <CompactTable.CellContent className="relative isolate">
                                        <div className="gap-xs flex items-start">
                                          <InternalLink className="after:absolute after:inset-0" href={href} prefetch="on-hover-debounced">
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                          </InternalLink>
                                          <SllBadge chainId={row.original.market.chainId} className="relative z-10 size-5" marketId={row.original.market.marketId} source="market_explore" />
                                        </div>
                                      </CompactTable.CellContent> : <InternalLink href={href} prefetch="on-hover-debounced">
                                        <CompactTable.CellContent>
                                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </CompactTable.CellContent>
                                      </InternalLink>}
                                  </CompactTable.Cell>)}
                              </CompactTable.Row>;
                  })}
                          {paddingBottom > 0 && <tr>
                              <td colSpan={table.getVisibleLeafColumns().length} style={{
                      height: paddingBottom
                    }} />
                            </tr>}
                        </>}

                      {skeletonRows.map((row, index) => <CompactTable.SkeletonRow key={`${index}-skeleton-market`} cells={row} keyPrefix={`${index}-skeleton-market`} />)}
                    </CompactTable.Body>
                  </CompactTable>}

                {showEmptyState && <TableNoFilterResults type="markets" withBackground hasFilters={hasFilters} clearFilters={clearFilters} height="100%" />}

                {hasError && (isFilterTooLong ? <TableNoFilterResults type="markets" withBackground hasFilters clearFilters={clearFilters} height="100%" message={FILTER_TOO_LONG_MESSAGE} /> : <TableQueryError error={error} minHeight="688px" style={{
              borderRadius: '0',
              backgroundColor: 'transparent'
            }} />)}

                {hasNextPage && !hasError && !inWalletFilterEmptyState && <div ref={sentinelRef} className="h-px" />}
              </div>
            </div>}
        </div>
      </div>
    </div>;
};
export default TableMarketClient;